import { Component, computed, input, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  LeaderboardAward,
  LeaderboardEntry,
  Pick,
  awardValue,
  awardsForPick,
  prizeDescriptionsFor,
} from '../../../core/competitions/competitions';
import { ageFor } from '../../../core/people/people';

/** One of the viewer's own picks, with its per-pick score breakdown
 *  merged in from the leaderboard entry (F-015). */
interface MyPickRow {
  pick: Pick;
  age: number | null;
  points: number;
  prizeDescriptions: string[];
  awards: LeaderboardAward[];
}

/** F-015 — read-only "your picks" summary on the competition detail page:
 *  a CTA to enter/manage picks (or none, past the cutoff) plus a table of
 *  already-submitted picks with points/prizes/achievement notes. Purely
 *  presentational — the parent already has `picks` and `leaderboardEntry`
 *  loaded (same endpoints back the admin awards panel and the standalone
 *  leaderboard page), so this doesn't fetch anything of its own. */
@Component({
  selector: 'app-competition-my-picks',
  imports: [RouterLink],
  templateUrl: './competition-my-picks.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './competition-my-picks.css',
})
export class CompetitionMyPicks {
  readonly competitionId = input.required<number>();
  readonly picks = input<Pick[]>([]);
  readonly leaderboardEntry = input<LeaderboardEntry | null>(null);
  readonly pastCutoff = input(false);
  readonly awardValue = awardValue;

  /** Awards earned by the overall situation rather than by any one pick
   *  (Reap Baby Reap, Dropping Like Flies, Grim Reaper) — they have no row
   *  in the picks table, so they're listed on their own. */
  readonly otherAwards = computed<LeaderboardAward[]>(
    () => this.leaderboardEntry()?.awards.filter((a) => a.pickId === null) ?? [],
  );

  readonly rows = computed<MyPickRow[]>(() => {
    const entry = this.leaderboardEntry();
    return this.picks().map((pick) => {
      const awards = entry ? awardsForPick(entry.awards, pick.id) : [];
      const points = awards.reduce((sum, a) => sum + (a.points ?? 0), 0);
      const prizeDescriptions = prizeDescriptionsFor(awards);
      return { pick, age: ageFor(pick.person), points, prizeDescriptions, awards };
    });
  });

  /** "Enter" before any picks, "Manage" once there's at least one, and no
   *  button at all once the cutoff passes — the table above it already
   *  shows what was submitted, read-only. */
  readonly buttonLabel = computed<'Enter your picks' | 'Manage your picks' | null>(() => {
    if (this.pastCutoff()) return null;
    return this.picks().length === 0 ? 'Enter your picks' : 'Manage your picks';
  });

  dateOnly(iso: string): string {
    return iso.slice(0, 10);
  }
}
